/**
 * M.A.N.A Product Reviews — Firebase Backend
 * ============================================
 * Handles review submission, loading, and image uploads.
 * Uses Firebase Firestore for data and Firebase Storage for images.
 */
(function () {
    'use strict';

    /* -----------------------------------------------
       Firebase Configuration
       Replace these values with your Firebase project credentials.
       Get them from: Firebase Console → Project Settings → Web App
       ----------------------------------------------- */
    var firebaseConfig = {
        apiKey: "AIzaSyAC5S72IDVH4y3DogpCvEYKDh0hUzKznv0",
        authDomain: "mana-66ae0.firebaseapp.com",
        projectId: "mana-66ae0",
        storageBucket: "mana-66ae0.firebasestorage.app",
        messagingSenderId: "38475338187",
        appId: "1:38475338187:web:d15d32df4484e34e89a64e",
        measurementId: "G-XYD3MWPKQF"
    };

    var db = null;
    var storage = null;
    var isInitialized = false;

    /* -----------------------------------------------
       Initialize Firebase (called once)
       ----------------------------------------------- */
    function initFirebase() {
        if (isInitialized) return;
        try {
            if (typeof firebase === 'undefined') {
                console.warn('[M.A.N.A Reviews] Firebase SDK not loaded');
                return;
            }
            if (!firebase.apps.length) {
                firebase.initializeApp(firebaseConfig);
            }
            db = firebase.firestore();
            storage = firebase.storage();
            isInitialized = true;
        } catch (e) {
            console.error('[M.A.N.A Reviews] Firebase init error:', e);
        }
    }

    /* -----------------------------------------------
       Upload an image to Firebase Storage
       Returns a download URL string
       ----------------------------------------------- */
    function uploadImage(file, productId) {
        return new Promise(function (resolve, reject) {
            if (!storage) { reject('Storage not initialized'); return; }
            var fileName = Date.now() + '_' + file.name.replace(/[^a-zA-Z0-9.]/g, '_');
            var ref = storage.ref('reviews/' + productId + '/' + fileName);
            var task = ref.put(file);
            task.on('state_changed',
                null,
                function (error) { reject(error); },
                function () {
                    task.snapshot.ref.getDownloadURL().then(resolve).catch(reject);
                }
            );
        });
    }

    /* -----------------------------------------------
       Submit a review
       ----------------------------------------------- */
    function submitReview(data) {
        return new Promise(function (resolve, reject) {
            if (!db) { reject('Firestore not initialized'); return; }

            var productId = data.productId;
            var files = data.images || [];

            /* Upload images first (if any) */
            var imagePromises = [];
            for (var i = 0; i < files.length && i < 3; i++) {
                imagePromises.push(uploadImage(files[i], productId));
            }

            Promise.all(imagePromises).then(function (imageUrls) {
                var review = {
                    productId: productId,
                    author: data.author || 'Anonymous',
                    email: data.email || '',
                    rating: parseInt(data.rating) || 5,
                    comment: data.comment || '',
                    imageUrls: imageUrls,
                    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
                    verified: false
                };

                db.collection('reviews').add(review).then(function (docRef) {
                    /* Update aggregate stats */
                    updateProductStats(productId).then(function () {
                        resolve(docRef.id);
                    });
                }).catch(reject);
            }).catch(reject);
        });
    }

    /* -----------------------------------------------
       Load reviews for a product
       ----------------------------------------------- */
    function loadReviews(productId, sortBy) {
        return new Promise(function (resolve, reject) {
            if (!db) { reject('Firestore not initialized'); return; }

            var query = db.collection('reviews')
                .where('productId', '==', String(productId));

            if (sortBy === 'highest') {
                query = query.orderBy('rating', 'desc');
            } else if (sortBy === 'lowest') {
                query = query.orderBy('rating', 'asc');
            } else {
                query = query.orderBy('createdAt', 'desc');
            }

            query.get().then(function (snapshot) {
                var reviews = [];
                snapshot.forEach(function (doc) {
                    var d = doc.data();
                    d.id = doc.id;
                    /* Convert Firestore timestamp to readable date */
                    if (d.createdAt && d.createdAt.toDate) {
                        d.dateFormatted = d.createdAt.toDate().toLocaleDateString('en-US', {
                            year: 'numeric', month: 'short', day: 'numeric'
                        });
                    } else {
                        d.dateFormatted = 'Just now';
                    }
                    reviews.push(d);
                });
                resolve(reviews);
            }).catch(reject);
        });
    }

    /* -----------------------------------------------
       Load aggregate stats for a product
       ----------------------------------------------- */
    function loadStats(productId) {
        return new Promise(function (resolve, reject) {
            if (!db) { resolve({ avg: 0, count: 0 }); return; }

            db.collection('review_stats').doc(String(productId)).get()
                .then(function (doc) {
                    if (doc.exists) {
                        resolve(doc.data());
                    } else {
                        resolve({ avg: 0, count: 0 });
                    }
                }).catch(function () {
                    resolve({ avg: 0, count: 0 });
                });
        });
    }

    /* -----------------------------------------------
       Update aggregate stats after a new review
       ----------------------------------------------- */
    function updateProductStats(productId) {
        return db.collection('reviews')
            .where('productId', '==', String(productId))
            .get()
            .then(function (snapshot) {
                var total = 0;
                var count = 0;
                snapshot.forEach(function (doc) {
                    total += doc.data().rating;
                    count++;
                });
                var avg = count > 0 ? Math.round((total / count) * 10) / 10 : 0;
                return db.collection('review_stats').doc(String(productId)).set({
                    avg: avg,
                    count: count,
                    productId: String(productId)
                });
            });
    }

    /* -----------------------------------------------
       Generate star HTML
       ----------------------------------------------- */
    function starsHTML(rating, interactive) {
        var html = '';
        for (var i = 1; i <= 5; i++) {
            var cls = 'mana-review__star';
            if (i <= rating) cls += ' is-filled';
            if (interactive) cls += ' is-interactive';
            html += '<span class="' + cls + '" data-rating="' + i + '">' +
                '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="' +
                (i <= rating ? 'currentColor' : 'none') +
                '" stroke="currentColor" stroke-width="1.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>' +
                '</span>';
        }
        return html;
    }

    /* -----------------------------------------------
       Render a single review card
       ----------------------------------------------- */
    function renderReview(review) {
        var imagesHtml = '';
        if (review.imageUrls && review.imageUrls.length > 0) {
            imagesHtml = '<div class="mana-review__images">';
            review.imageUrls.forEach(function (url) {
                imagesHtml += '<div class="mana-review__image-thumb" data-full="' + url + '">' +
                    '<img src="' + url + '" alt="Review image" loading="lazy">' +
                    '</div>';
            });
            imagesHtml += '</div>';
        }

        return '<div class="mana-review__card">' +
            '<div class="mana-review__card-header">' +
            '<div class="mana-review__card-author">' +
            '<span class="mana-review__card-name">' + escapeHtml(review.author) + '</span>' +
            (review.verified ? '<span class="mana-review__verified">Verified</span>' : '') +
            '</div>' +
            '<span class="mana-review__card-date">' + review.dateFormatted + '</span>' +
            '</div>' +
            '<div class="mana-review__card-stars">' + starsHTML(review.rating) + '</div>' +
            '<p class="mana-review__card-comment">' + escapeHtml(review.comment) + '</p>' +
            imagesHtml +
            '</div>';
    }

    function escapeHtml(str) {
        var div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }

    /* -----------------------------------------------
       Initialize the Review UI on the product page
       ----------------------------------------------- */
    function initReviewUI() {
        var container = document.getElementById('mana-reviews');
        if (!container) return;

        initFirebase();

        var productId = container.dataset.productId;
        var reviewList = container.querySelector('#mana-review-list');
        var summaryAvg = container.querySelector('#mana-review-avg');
        var summaryCount = container.querySelector('#mana-review-count');
        var summaryStars = container.querySelector('#mana-review-stars');
        var sortSelect = container.querySelector('#mana-review-sort');
        var writeBtn = container.querySelector('#mana-review-write-btn');
        var formWrap = container.querySelector('#mana-review-form-wrap');
        var cancelBtn = container.querySelector('#mana-review-cancel');
        var submitBtn = container.querySelector('#mana-review-submit');
        var starsInput = container.querySelectorAll('#mana-review-rating .mana-review__star');
        var fileInput = container.querySelector('#mana-review-images');
        var filePreview = container.querySelector('#mana-review-image-preview');
        var selectedRating = 0;
        var selectedFiles = [];

        /* Load stats */
        loadStats(productId).then(function (stats) {
            if (summaryAvg) summaryAvg.textContent = stats.avg > 0 ? stats.avg.toFixed(1) : '—';
            if (summaryCount) summaryCount.textContent = stats.count + ' review' + (stats.count !== 1 ? 's' : '');
            if (summaryStars) summaryStars.innerHTML = starsHTML(Math.round(stats.avg));
        });

        /* Load and render reviews */
        function renderAll(sort) {
            if (!reviewList) return;
            reviewList.innerHTML = '<p class="mana-review__loading">Loading reviews…</p>';
            loadReviews(productId, sort).then(function (reviews) {
                if (reviews.length === 0) {
                    reviewList.innerHTML = '<p class="mana-review__empty">No reviews yet. Be the first to share your experience!</p>';
                    return;
                }
                var html = '';
                reviews.forEach(function (r) { html += renderReview(r); });
                reviewList.innerHTML = html;

                /* Image lightbox */
                reviewList.querySelectorAll('.mana-review__image-thumb').forEach(function (thumb) {
                    thumb.addEventListener('click', function () {
                        var lb = document.getElementById('mana-review-lightbox');
                        var lbImg = document.getElementById('mana-review-lightbox-img');
                        if (lb && lbImg) {
                            lbImg.src = thumb.dataset.full;
                            lb.classList.add('is-open');
                        }
                    });
                });
            }).catch(function () {
                reviewList.innerHTML = '<p class="mana-review__empty">Unable to load reviews.</p>';
            });
        }

        renderAll('newest');

        /* Sort */
        if (sortSelect) {
            sortSelect.addEventListener('change', function () {
                renderAll(sortSelect.value);
            });
        }

        /* Toggle form */
        if (writeBtn && formWrap) {
            writeBtn.addEventListener('click', function () {
                formWrap.classList.toggle('is-open');
                writeBtn.style.display = formWrap.classList.contains('is-open') ? 'none' : '';
            });
        }
        if (cancelBtn && formWrap) {
            cancelBtn.addEventListener('click', function () {
                formWrap.classList.remove('is-open');
                if (writeBtn) writeBtn.style.display = '';
            });
        }

        /* Star rating selector */
        starsInput.forEach(function (star) {
            star.addEventListener('click', function () {
                selectedRating = parseInt(star.dataset.rating);
                starsInput.forEach(function (s) {
                    var r = parseInt(s.dataset.rating);
                    var svg = s.querySelector('svg');
                    if (r <= selectedRating) {
                        s.classList.add('is-filled');
                        svg.setAttribute('fill', 'currentColor');
                    } else {
                        s.classList.remove('is-filled');
                        svg.setAttribute('fill', 'none');
                    }
                });
            });
        });

        /* File input */
        if (fileInput) {
            fileInput.addEventListener('change', function () {
                selectedFiles = Array.from(fileInput.files).slice(0, 3);
                if (filePreview) {
                    filePreview.innerHTML = '';
                    selectedFiles.forEach(function (f) {
                        var reader = new FileReader();
                        reader.onload = function (e) {
                            var img = document.createElement('img');
                            img.src = e.target.result;
                            img.className = 'mana-review__preview-img';
                            filePreview.appendChild(img);
                        };
                        reader.readAsDataURL(f);
                    });
                }
            });
        }

        /* Submit */
        if (submitBtn) {
            submitBtn.addEventListener('click', function () {
                var nameField = container.querySelector('#mana-review-name');
                var emailField = container.querySelector('#mana-review-email');
                var commentField = container.querySelector('#mana-review-comment');

                if (selectedRating === 0) { alert('Please select a star rating.'); return; }
                if (!commentField || commentField.value.trim() === '') { alert('Please write a comment.'); return; }

                submitBtn.disabled = true;
                submitBtn.textContent = 'Submitting…';

                submitReview({
                    productId: productId,
                    author: nameField ? nameField.value.trim() : 'Anonymous',
                    email: emailField ? emailField.value.trim() : '',
                    rating: selectedRating,
                    comment: commentField.value.trim(),
                    images: selectedFiles
                }).then(function () {
                    submitBtn.textContent = 'Thank you!';
                    formWrap.classList.remove('is-open');
                    if (writeBtn) writeBtn.style.display = '';
                    /* Reload */
                    renderAll('newest');
                    loadStats(productId).then(function (stats) {
                        if (summaryAvg) summaryAvg.textContent = stats.avg.toFixed(1);
                        if (summaryCount) summaryCount.textContent = stats.count + ' review' + (stats.count !== 1 ? 's' : '');
                        if (summaryStars) summaryStars.innerHTML = starsHTML(Math.round(stats.avg));
                    });
                    /* Reset form */
                    if (nameField) nameField.value = '';
                    if (emailField) emailField.value = '';
                    commentField.value = '';
                    selectedRating = 0;
                    selectedFiles = [];
                    if (fileInput) fileInput.value = '';
                    if (filePreview) filePreview.innerHTML = '';
                    starsInput.forEach(function (s) {
                        s.classList.remove('is-filled');
                        s.querySelector('svg').setAttribute('fill', 'none');
                    });
                    setTimeout(function () {
                        submitBtn.disabled = false;
                        submitBtn.textContent = 'Submit Review';
                    }, 2000);
                }).catch(function (err) {
                    console.error('[M.A.N.A Reviews] Submit error:', err);
                    submitBtn.disabled = false;
                    submitBtn.textContent = 'Submit Review';
                    alert('Something went wrong. Please try again.');
                });
            });
        }

        /* Lightbox close */
        var lb = document.getElementById('mana-review-lightbox');
        if (lb) {
            lb.addEventListener('click', function () {
                lb.classList.remove('is-open');
            });
        }
    }

    /* Expose init */
    window.ManaReviews = { init: initReviewUI };

    /* Auto-init on DOMContentLoaded */
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initReviewUI);
    } else {
        initReviewUI();
    }
})();
